import {
  map
} from 'rxjs/operators';
import {
  Injectable
} from "@angular/core";
import {
  UserDnaClient
} from "../../models/dna/dna.user-dna-client.model";
import {
  HttpClient
} from "@angular/common/http";
import {
  UserService
} from "./user.service";
import {
  DnaFilteredUserDna
} from "../../models/dna/dna.filtered-user-dna.model";
import {
  ApplicationError
} from "../../shared/handlers/applicationerror";
import {
  stringifyOnce
} from "../../shared/utils/jsonstringfyonce.util";
import {
  ISelectedDna
} from "../../shared/components/dnaselector/dnaselector.component";
import {
  AuthenticatedModel
} from "../../models/authentication/authenticated.model";
import {
  BehaviorSubject,
  Subject
} from "rxjs";

@Injectable({ providedIn: "root" })
export class DnaService {

  private dnaLocalStorageName = 'dnaStorage';

  private dnaSubject = new Subject<DnaFilteredUserDna[]>();

  public loggedInUser: AuthenticatedModel;

  public filteredUserDnaList = this.dnaSubject.asObservable();
  //we should create a type for this after renaming uname from dnaclient table
  // tslint:disable-next-line: no-use-before-declare
  public defaultDnaObservable = new BehaviorSubject<DefaultDnaModel>(new DefaultDnaModel());

  public defaultDna: DefaultDnaModel;

  constructor(private http: HttpClient,
    private userService: UserService) {

  }

  public initializeDnaService() {
    this.userService.getUserObs.subscribe((user: AuthenticatedModel) => {
      this.loggedInUser = user;
      this.getDefaultDna();
    });

    if (this.loggedInUser) {
      this.setDnaToStorage();
    }
  }

  private service = 'DnaService';

  public getDna(): Array<DnaFilteredUserDna> {
    let userDnaClient = new Array<DnaFilteredUserDna>();
    const dnaInfo = localStorage.getItem(this.dnaLocalStorageName);

    if (dnaInfo) {
      this.dnaSubject.next(Object.assign(userDnaClient,
        JSON.parse(localStorage.getItem(this.dnaLocalStorageName))));
      return Object.assign(userDnaClient, JSON.parse(localStorage.getItem(this.dnaLocalStorageName)));
    } else {
      userDnaClient = this.setDnaToStorage();
    }

    return userDnaClient;
  }

  public getCurrentDefaultDna() {
    return this.defaultDna;
  }

  public forceRefreshDna() {
    this.clear();
    this.getDna();
  }

  public getDefaultDna() {
    this.dnaSubject.subscribe((dnaList: Array<DnaFilteredUserDna>) => {

      const userDnaClient = dnaList;

      if (userDnaClient && userDnaClient.length > 0 && userDnaClient.filter(p => p.isDefault)) {
        this.defaultDna = {
          clientName: userDnaClient.filter(p => p.isDefault)[0].clientName,
          clientId: userDnaClient.filter(p => p.isDefault)[0].clientId,
          dnaName: userDnaClient.filter(p => p.isDefault)[0].uname,
          dnaId: userDnaClient.filter(p => p.isDefault)[0].dnaClientId,
          dnaLogo: userDnaClient.filter(p => p.isDefault)[0].dnaLogo,
          dnaLogoBase64: userDnaClient.filter(p => p.isDefault)[0].dnaLogoBase64,
          masterLegendPath: userDnaClient.filter(p => p.isDefault)[0].masterLegendPath,
          colour1: userDnaClient.filter(p => p.isDefault)[0].colour1,
          colour2: userDnaClient.filter(p => p.isDefault)[0].colour2,
          colour3: userDnaClient.filter(p => p.isDefault)[0].colour3,
          colour4: userDnaClient.filter(p => p.isDefault)[0].colour4,
          colour5: userDnaClient.filter(p => p.isDefault)[0].colour5,
          legacyIcons: userDnaClient.filter(p => p.isDefault)[0].legacyIcons,
          designAutomatorIcons: userDnaClient.filter(p => p.isDefault)[0].designAutomatorIcons,
          customIcons: userDnaClient.filter(p => p.isDefault)[0].customIcons,
          currentVersion: userDnaClient.filter(p => p.isDefault)[0].currentVersion,
          displayName: userDnaClient.filter(p => p.isDefault)[0].displayName,
        };
        this.defaultDnaObservable.next(this.defaultDna);
      }
    });
  }

  public updateDefaultDnaWithPassedDna(dna: DnaFilteredUserDna) {
    if (dna && dna.isDefault) {
      this.defaultDna = {
        clientName: dna.clientName,
        clientId: dna.clientId,
        dnaName: dna.uname,
        dnaId: dna.dnaClientId,
        dnaLogo: dna.dnaLogo,
        dnaLogoBase64: dna.dnaLogoBase64,
        masterLegendPath: dna.masterLegendPath,
        colour1: dna.colour1,
        colour2: dna.colour2,
        colour3: dna.colour3,
        colour4: dna.colour4,
        colour5: dna.colour5,
        legacyIcons: dna.legacyIcons,
        designAutomatorIcons: dna.designAutomatorIcons,
        customIcons: dna.customIcons,
        currentVersion: dna.currentVersion,
        displayName: dna.displayName
      };
      this.defaultDnaObservable.next(this.defaultDna);
    }
  }

  public setDnaToStorage() {
    let tempUserDnaClient = new Array<DnaFilteredUserDna>();

    this.getDnaClientsOfUser()
      .subscribe((userDnaClient: UserDnaClient[]) => {
        tempUserDnaClient = this.createFilteredDna(userDnaClient);
        this.dnaSubject.next(tempUserDnaClient);

        localStorage.setItem(this.dnaLocalStorageName,
          stringifyOnce(
            tempUserDnaClient,
            '',
            ''));
      });

    return tempUserDnaClient;
  }

  public clear() {
    localStorage.removeItem(this.dnaLocalStorageName);
  }

  public changeDefaultDnaOfUser(selectedDna: ISelectedDna) {
    if (this.loggedInUser === null) {
      throw new ApplicationError('Logged in user can\'t be loaded. Refresh the page and try again');
    }

    if (!selectedDna || selectedDna.dnaId === 0) {
      throw new ApplicationError('Select a Dna from Dna panel to continue');
    }

    this.updateUserDefaultDna(selectedDna).subscribe(() => {
      localStorage.removeItem(this.dnaLocalStorageName);
      this.setDnaToStorage();
      this.getDefaultDna();
    });
  }

  public updateUserDefaultDna(selectedDna) {
    //let postObject = { 'newDefaultDna': selectedDna.dnaId };
    return this.http.post(`api/${this.service}/UpdateUserDefaultDna`, selectedDna.dnaId, { responseType: 'text' });
  }

  public getDnaClientsOfUser() {
    return this.http.get<UserDnaClient[]>(`api/${this.service}/GetUserDnaForStorage`).pipe(
      map((response) => response));
  }

  public createFilteredDna(userDna) {
    let filteredUserDna = new Array<DnaFilteredUserDna>();
    userDna.forEach((item) => {
      const tempItem: DnaFilteredUserDna = {
        isDefault: item.isDefault,
        clientId: item.dnaClient.clientId,
        uname: item.dnaClient.uname,
        dnaLogo: item.dnaClient.dnaLogo,
        dnaLogoBase64: item.dnaClient.dnaLogoBase64,
        masterLegendPath: item.dnaClient.masterLegendPath,
        colour1: item.dnaClient.colour1,
        colour2: item.dnaClient.colour2,
        colour3: item.dnaClient.colour3,
        colour4: item.dnaClient.colour4,
        colour5: item.dnaClient.colour5,
        naming: item.dnaClient.naming,
        clientName: item.dnaClient.clientName,
        clientAddress1: item.dnaClient.clientAddress1,
        clientAddress2: item.dnaClient.clientAddress2,
        clientCountry: item.dnaClient.clientCountry,
        clientPostCode: item.dnaClient.clientPostCode,
        dnaClientId: item.dnaClientId,
        legacyIcons: item.dnaClient.legacyIcons,
        designAutomatorIcons: item.dnaClient.designAutomatorIcons,
        customIcons: item.dnaClient.customIcons,
        currentVersion: item.dnaClient.currentVersion,
        displayName: item.dnaClient.currentVersion && item.dnaClient.currentVersion > 1 ? `${item.dnaClient.uname} V${item.dnaClient.currentVersion}` : item.dnaClient.uname,
      };
      filteredUserDna.push(tempItem);
    });

    return filteredUserDna;
  }

  public getAssignedSystemTypes(dnaClientId: number) {
    return this.http.get<number[]>(`api/${this.service}/GetAssignedSystemTypes/${dnaClientId}`);
  }
}

export class DefaultDnaModel {
  clientName: string;
  clientId: number;
  dnaName: string;
  dnaId: number;
  dnaLogo: string;
  dnaLogoBase64: string;
  masterLegendPath: string;
  colour1: string;
  colour2: string;
  colour3: string;
  colour4: string;
  colour5: string;
  legacyIcons: boolean;
  designAutomatorIcons: boolean;
  customIcons: boolean;
  currentVersion: number;
  displayName: string;
}
